import React, { useState } from 'react';
import {
   Paper,
   Box,
   Typography,
   TextField,
   Switch,
   FormControlLabel,
   FormGroup,
   Checkbox,
   Button,
   Grid,
   Divider,
   InputAdornment,
   Card,
   CardContent,
   List,
   ListItem,
   ListItemIcon,
   ListItemText,
   Alert,
   CircularProgress,
} from '@mui/material';
import {
   CheckCircle as CheckCircleIcon,
   Save as SaveIcon,
   Cancel as CancelIcon,
   Add as AddIcon,
   CheckBox as CheckBoxIcon,
   Event as EventIcon,
   Discount as DiscountIcon,
   Lock as LockIcon,
   Star as StarIcon,
   Support as SupportIcon,
} from '@mui/icons-material';
import { collection, addDoc, updateDoc, doc, Timestamp } from 'firebase/firestore';
import { db } from '../../../../firebase';
import { SubscriptionTier } from '../index';
import { formatCurrency } from '../../../../utils/formatting';

interface SubscriptionTierFormProps {
   organizationId: string;
   tier?: SubscriptionTier; // If provided, we're editing an existing tier
   onCancel: () => void;
   onSuccess: () => void;
}

const SubscriptionTierForm: React.FC<SubscriptionTierFormProps> = ({
   organizationId,
   tier,
   onCancel,
   onSuccess,
}) => {
   // Form state
   const [formData, setFormData] = useState<Partial<SubscriptionTier>>(
      tier || {
         name: '',
         monthlyPrice: 9.99,
         annualPrice: 99.99,
         description: '',
         benefits: {
            earlyAccess: true,
            discounts: true,
            privateEvents: false,
            exclusiveContent: false,
            prioritySupport: false,
         },
         memberLimit: 100,
         unlimitedMembers: true,
         active: true,
         isPrivateForMembers: false,
      }
   );

   // Form state
   const [loading, setLoading] = useState(false);
   const [error, setError] = useState<string | null>(null);
   const [customBenefitInput, setCustomBenefitInput] = useState('');

   // Calculate annual discount percentage
   const annualDiscountPercentage =
      formData.monthlyPrice && formData.annualPrice
         ? Math.round(100 - (formData.annualPrice / (formData.monthlyPrice * 12)) * 100)
         : 0;

   // Handle text fields change
   const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target;
      setFormData({
         ...formData,
         [name]: value,
      });
   };

   // Handle number fields change
   const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target;
      let parsedValue = parseFloat(value);

      // Ensure we have valid numbers
      if (isNaN(parsedValue) || parsedValue < 0) {
         parsedValue = 0;
      }

      setFormData({
         ...formData,
         [name]: parsedValue,
      });
   };

   // Handle benefit checkbox changes
   const handleBenefitChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, checked } = e.target;
      setFormData({
         ...formData,
         benefits: {
            ...formData.benefits!,
            [name]: checked,
         },
      });
   };

   const handleCustomBenefitChange = (
      customBenefitType: string,
      customBenefit: string,
      checked: boolean
   ) => {
      setFormData((prevFormData: any) => ({
         ...prevFormData,
         benefits: {
            ...prevFormData.benefits!,
            [customBenefitType]: {
               ...(prevFormData.benefits![customBenefitType] || {}),
               [customBenefit]: checked,
            },
         },
      }));
   };

   // Handle switch changes
   const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, checked } = e.target;
      setFormData({
         ...formData,
         [name]: checked,
      });
   };

   // Handle custom benefit add
   const handleAddCustomBenefit = () => {
      if (!customBenefitInput.trim()) return;

      if (!formData.benefits?.customBenefit1) {
         setFormData({
            ...formData,
            benefits: {
               ...formData.benefits!,
               customBenefit1: {
                  ...formData?.benefits?.customBenefit1!,
                  [customBenefitInput.charAt(0).toUpperCase() + customBenefitInput.slice(1)]: true,
               },
               //  [customBenefitInput]: true,
            },
         });
      } else if (!formData.benefits?.customBenefit2) {
         setFormData({
            ...formData,
            benefits: {
               ...formData.benefits!,
               customBenefit2: {
                  ...formData?.benefits?.customBenefit2!,
                  [customBenefitInput.charAt(0).toUpperCase() + customBenefitInput.slice(1)]: true,
               },
               //  customBenefit2: customBenefitInput,
            },
         });
      }

      setCustomBenefitInput('');
   };

   // Remove custom benefit
   const handleRemoveCustomBenefit = (benefitKey: 'customBenefit1' | 'customBenefit2') => {
      const newBenefits = { ...formData.benefits! };
      delete newBenefits[benefitKey];

      // If we're removing benefit1 but have benefit2, move benefit2 to benefit1
      if (benefitKey === 'customBenefit1' && newBenefits.customBenefit2) {
         newBenefits.customBenefit1 = newBenefits.customBenefit2;
         delete newBenefits.customBenefit2;
      }

      setFormData({
         ...formData,
         benefits: newBenefits,
      });
   };

   // Save tier to database
   const handleSaveTier = async () => {
      try {
         setLoading(true);
         setError(null);

         // Validate form
         if (!formData.name?.trim()) {
            setError('Please provide a tier name');
            setLoading(false);
            return;
         }

         if (!formData.monthlyPrice || formData.monthlyPrice <= 0) {
            setError('Please set a valid monthly price');
            setLoading(false);
            return;
         }

         if (!formData.annualPrice || formData.annualPrice <= 0) {
            setError('Please set a valid annual price');
            setLoading(false);
            return;
         }

         if (!formData.unlimitedMembers && (!formData.memberLimit || formData.memberLimit <= 0)) {
            setError('Please set a valid member limit or choose unlimited members');
            setLoading(false);
            return;
         }

         const _customBenefits = {
            ...(formData.benefits?.customBenefit1 || {}),
            ...(formData.benefits?.customBenefit2 || {}),
         };

         delete formData.benefits?.customBenefit1;
         delete formData.benefits?.customBenefit2;

         const response = await fetch(
            `${process.env.REACT_APP_API_URL}/api/organization/create-subscription-tier`,
            {
               method: 'POST',
               headers: {
                  'Content-Type': 'application/json',
               },
               body: JSON.stringify({
                  organizationId,
                  monthlyPrice: formData.monthlyPrice,
                  annualPrice: formData.annualPrice,
                  title: formData.name,
                  description: formData.description,
                  benefits: {
                     ...formData.benefits,
                     ..._customBenefits,
                  },
                  memberLimit: formData.memberLimit,
                  unlimitedMembers: formData.unlimitedMembers,
                  active: formData.active,
                  isPrivateForMembers: formData.isPrivateForMembers,
               }),
            }
         );

         const data = await response.json();

         if (data.status == 400) {
            setError(data.message);
         } else {
            onSuccess();
         }

         // const now = Timestamp.now();

         // if (tier?.id) {
         //   // Update existing tier
         //   const tierRef = doc(db, `organizations/${organizationId}/subscriptionTiers/${tier.id}`);
         //   await updateDoc(tierRef, {
         //     ...formData,
         //     updatedAt: now
         //   });
         // } else {
         //   // Create new tier
         //   const tiersCollection = collection(db, `organizations/${organizationId}/subscriptionTiers`);
         //   await addDoc(tiersCollection, {
         //     ...formData,
         //     createdAt: now,
         //     updatedAt: now
         //   });
         // }

         // Success callback
      } catch (err) {
         setError('Failed to save subscription tier. Please try again.');
      } finally {
         setLoading(false);
      }
   };

   return (
      <Grid container spacing={3}>
         <Grid item xs={12} md={7}>
            <Paper sx={{ p: 3 }}>
               <Typography variant='h6' component='h3' gutterBottom>
                  {tier ? 'Edit' : 'Create'} Subscription Tier
               </Typography>

               {error && (
                  <Alert severity='error' sx={{ mb: 3 }}>
                     {error}
                  </Alert>
               )}

               <Box component='form' sx={{ mt: 3 }}>
                  <Grid container spacing={3}>
                     {/* Tier Name */}
                     <Grid item xs={12}>
                        <TextField
                           name='name'
                           label='Tier Name'
                           fullWidth
                           value={formData.name}
                           onChange={handleInputChange}
                           placeholder='e.g., Basic, Premium, Pro, VIP'
                           required
                        />
                     </Grid>

                     {/* Monthly & Annual Price */}
                     <Grid item xs={12} sm={6}>
                        <TextField
                           name='monthlyPrice'
                           label='Monthly Price'
                           type='number'
                           fullWidth
                           value={formData.monthlyPrice}
                           onChange={handleNumberChange}
                           InputProps={{
                              startAdornment: <InputAdornment position='start'>$</InputAdornment>,
                           }}
                           required
                        />
                     </Grid>

                     <Grid item xs={12} sm={6}>
                        <TextField
                           name='annualPrice'
                           label='Annual Price'
                           type='number'
                           fullWidth
                           value={formData.annualPrice}
                           onChange={handleNumberChange}
                           InputProps={{
                              startAdornment: <InputAdornment position='start'>$</InputAdornment>,
                           }}
                           helperText={
                              annualDiscountPercentage > 0
                                 ? `${annualDiscountPercentage}% savings compared to monthly`
                                 : ''
                           }
                           required
                        />
                     </Grid>

                     {/* Description */}
                     <Grid item xs={12}>
                        <TextField
                           name='description'
                           label='Description'
                           fullWidth
                           multiline
                           rows={3}
                           value={formData.description}
                           onChange={handleInputChange}
                           placeholder='Describe what members get with this subscription tier'
                        />
                     </Grid>

                     <Grid item xs={12}>
                        <Divider sx={{ my: 1 }} />
                        <Typography variant='subtitle1' sx={{ mb: 2 }}>
                           Benefits
                        </Typography>

                        {/* Benefits Checkboxes */}
                        <FormGroup>
                           <Grid container spacing={2}>
                              <Grid item xs={12} sm={6}>
                                 <FormControlLabel
                                    control={
                                       <Checkbox
                                          checked={formData.benefits?.earlyAccess}
                                          onChange={handleBenefitChange}
                                          name='earlyAccess'
                                       />
                                    }
                                    label='Early Access to Events'
                                 />
                              </Grid>

                              <Grid item xs={12} sm={6}>
                                 <FormControlLabel
                                    control={
                                       <Checkbox
                                          checked={formData.benefits?.discounts}
                                          onChange={handleBenefitChange}
                                          name='discounts'
                                       />
                                    }
                                    label='Exclusive Discounts'
                                 />
                              </Grid>

                              <Grid item xs={12} sm={6}>
                                 <FormControlLabel
                                    control={
                                       <Checkbox
                                          checked={formData.benefits?.privateEvents}
                                          onChange={handleBenefitChange}
                                          name='privateEvents'
                                       />
                                    }
                                    label='Private Events'
                                 />
                              </Grid>

                              <Grid item xs={12} sm={6}>
                                 <FormControlLabel
                                    control={
                                       <Checkbox
                                          checked={formData.benefits?.exclusiveContent}
                                          onChange={handleBenefitChange}
                                          name='exclusiveContent'
                                       />
                                    }
                                    label='Exclusive Content'
                                 />
                              </Grid>

                              <Grid item xs={12} sm={6}>
                                 <FormControlLabel
                                    control={
                                       <Checkbox
                                          checked={formData.benefits?.prioritySupport}
                                          onChange={handleBenefitChange}
                                          name='prioritySupport'
                                       />
                                    }
                                    label='Priority Support'
                                 />
                              </Grid>
                           </Grid>
                        </FormGroup>

                        {/* Custom Benefits */}
                        <Box sx={{ mt: 2 }}>
                           {/* Display custom benefits */}
                           {formData.benefits?.customBenefit1 &&
                              Object.keys(formData.benefits.customBenefit1).length > 0 &&
                              Object.keys(formData.benefits.customBenefit1).map((customBenefit) => {
                                 return (
                                    <Box
                                       key={customBenefit}
                                       sx={{ display: 'flex', alignItems: 'center', mt: 1, mb: 2 }}
                                    >
                                       <CheckBoxIcon color='primary' sx={{ mr: 1 }} />
                                       <Typography variant='body2'>{customBenefit}</Typography>
                                       <Button
                                          size='small'
                                          onClick={() =>
                                             handleRemoveCustomBenefit('customBenefit1')
                                          }
                                          sx={{ ml: 1 }}
                                       >
                                          Remove
                                       </Button>
                                    </Box>
                                 );
                              })}

                           {Object.keys(formData.benefits?.customBenefit2 || {}).length > 0 &&
                              Object.keys(formData.benefits?.customBenefit2 || {}).map(
                                 (customBenefit) => {
                                    return (
                                       <Box
                                          key={customBenefit}
                                          sx={{
                                             display: 'flex',
                                             alignItems: 'center',
                                             mt: 1,
                                             mb: 2,
                                          }}
                                       >
                                          <CheckBoxIcon color='primary' sx={{ mr: 1 }} />
                                          <Typography variant='body2'>{customBenefit}</Typography>
                                          <Button
                                             size='small'
                                             onClick={() =>
                                                handleRemoveCustomBenefit('customBenefit2')
                                             }
                                             sx={{ ml: 1 }}
                                          >
                                             Remove
                                          </Button>
                                       </Box>
                                    );
                                 }
                              )}
                           <Grid container spacing={1}>
                              <Grid item xs>
                                 <TextField
                                    fullWidth
                                    label='Add Custom Benefit'
                                    value={customBenefitInput}
                                    onChange={(e) => setCustomBenefitInput(e.target.value)}
                                    placeholder='e.g., Free monthly webinar'
                                 />
                              </Grid>
                              <Grid item>
                                 <Button
                                    variant='outlined'
                                    startIcon={<AddIcon />}
                                    onClick={handleAddCustomBenefit}
                                    disabled={
                                       !customBenefitInput.trim() ||
                                       (!!formData.benefits?.customBenefit1 &&
                                          !!formData.benefits?.customBenefit2)
                                    }
                                    sx={{ height: '56px' }}
                                 >
                                    Add
                                 </Button>
                              </Grid>
                           </Grid>
                        </Box>
                     </Grid>

                     <Grid item xs={12}>
                        <Divider sx={{ my: 1 }} />
                        <Typography variant='subtitle1' sx={{ mb: 2 }}>
                           Membership Limits
                        </Typography>

                        <FormControlLabel
                           control={
                              <Switch
                                 checked={formData.unlimitedMembers}
                                 onChange={handleSwitchChange}
                                 name='unlimitedMembers'
                              />
                           }
                           label='Unlimited Members'
                        />

                        {!formData.unlimitedMembers && (
                           <TextField
                              name='memberLimit'
                              label='Member Limit'
                              type='number'
                              value={formData.memberLimit}
                              onChange={handleNumberChange}
                              sx={{ mt: 2 }}
                              InputProps={{
                                 inputProps: { min: 1 },
                              }}
                           />
                        )}
                     </Grid>

                     <Grid item xs={12}>
                        <Divider sx={{ my: 1 }} />
                        <FormControlLabel
                           control={
                              <Switch
                                 checked={formData.active}
                                 onChange={handleSwitchChange}
                                 name='active'
                              />
                           }
                           label='Active'
                        />
                     </Grid>

                     <Grid item xs={12}>
                        <Divider sx={{ my: 1 }} />
                        <FormControlLabel
                           control={
                              <Switch
                                 checked={formData.isPrivateForMembers}
                                 onChange={handleSwitchChange}
                                 name='isPrivateForMembers'
                              />
                           }
                           label='Private for members (only visible to members)'
                        />
                     </Grid>

                     {/* Action Buttons */}
                     <Grid item xs={12}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                           <Button variant='outlined' startIcon={<CancelIcon />} onClick={onCancel}>
                              Cancel
                           </Button>

                           <Button
                              variant='contained'
                              startIcon={
                                 loading ? (
                                    <CircularProgress size={20} color='inherit' />
                                 ) : (
                                    <SaveIcon />
                                 )
                              }
                              onClick={handleSaveTier}
                              disabled={loading}
                           >
                              {tier ? 'Update' : 'Create'} Tier
                           </Button>
                        </Box>
                     </Grid>
                  </Grid>
               </Box>
            </Paper>
         </Grid>

         {/* Preview Panel */}
         <Grid item xs={12} md={5}>
            <Paper sx={{ p: 3 }}>
               <Typography variant='h6' component='h3' gutterBottom>
                  Preview
               </Typography>

               <Card sx={{ mt: 2, borderRadius: 2 }}>
                  <CardContent>
                     <Typography variant='h5' component='div' gutterBottom>
                        {formData.name || 'Tier Name'}
                     </Typography>

                     <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 2 }}>
                        <Typography variant='h4' component='div'>
                           {formatCurrency(formData.monthlyPrice || 0)}
                        </Typography>
                        <Typography variant='body2' color='text.secondary' sx={{ ml: 1 }}>
                           /month
                        </Typography>
                     </Box>

                     <Typography variant='body2' color='text.secondary' paragraph>
                        {formData.description ||
                           'Description of the subscription tier benefits and features.'}
                     </Typography>

                     <Box sx={{ mb: 2 }}>
                        <Typography variant='body2' sx={{ fontWeight: 'bold', mb: 1 }}>
                           Annual option: {formatCurrency(formData.annualPrice || 0)}/year
                           {annualDiscountPercentage > 0 && (
                              <span style={{ color: 'green', marginLeft: '8px' }}>
                                 Save {annualDiscountPercentage}%
                              </span>
                           )}
                        </Typography>
                     </Box>

                     <Divider sx={{ my: 2 }} />

                     <Typography variant='subtitle2' gutterBottom>
                        Benefits:
                     </Typography>

                     <List dense>
                        {formData.benefits?.earlyAccess && (
                           <ListItem disablePadding>
                              <ListItemIcon sx={{ minWidth: '30px' }}>
                                 <EventIcon color='primary' fontSize='small' />
                              </ListItemIcon>
                              <ListItemText primary='Early Access to Events' />
                           </ListItem>
                        )}

                        {formData.benefits?.discounts && (
                           <ListItem disablePadding>
                              <ListItemIcon sx={{ minWidth: '30px' }}>
                                 <DiscountIcon color='primary' fontSize='small' />
                              </ListItemIcon>
                              <ListItemText primary='Exclusive Discounts' />
                           </ListItem>
                        )}

                        {formData.benefits?.privateEvents && (
                           <ListItem disablePadding>
                              <ListItemIcon sx={{ minWidth: '30px' }}>
                                 <LockIcon color='primary' fontSize='small' />
                              </ListItemIcon>
                              <ListItemText primary='Private Events' />
                           </ListItem>
                        )}

                        {formData.benefits?.exclusiveContent && (
                           <ListItem disablePadding>
                              <ListItemIcon sx={{ minWidth: '30px' }}>
                                 <StarIcon color='primary' fontSize='small' />
                              </ListItemIcon>
                              <ListItemText primary='Exclusive Content' />
                           </ListItem>
                        )}

                        {formData.benefits?.prioritySupport && (
                           <ListItem disablePadding>
                              <ListItemIcon sx={{ minWidth: '30px' }}>
                                 <SupportIcon color='primary' fontSize='small' />
                              </ListItemIcon>
                              <ListItemText primary='Priority Support' />
                           </ListItem>
                        )}

                        {formData.benefits?.customBenefit1 &&
                           Object.keys(formData.benefits?.customBenefit1 || {}).length > 0 &&
                           Object.keys(formData.benefits?.customBenefit1 || {}).map(
                              (customBenefit) => {
                                 return (
                                    <ListItem disablePadding key={customBenefit}>
                                       <ListItemIcon sx={{ minWidth: '30px' }}>
                                          <CheckCircleIcon color='primary' fontSize='small' />
                                       </ListItemIcon>
                                       <ListItemText primary={customBenefit} />
                                    </ListItem>
                                 );
                              }
                           )}

                        {Object.keys(formData.benefits?.customBenefit2 || {}).length > 0 &&
                           Object.keys(formData.benefits?.customBenefit2 || {}).map(
                              (customBenefit) => {
                                 return (
                                    <ListItem disablePadding key={customBenefit}>
                                       <ListItemIcon sx={{ minWidth: '30px' }}>
                                          <CheckCircleIcon color='primary' fontSize='small' />
                                       </ListItemIcon>
                                       <ListItemText primary={customBenefit} />
                                    </ListItem>
                                 );
                              }
                           )}
                     </List>

                     {!formData.unlimitedMembers && formData.memberLimit && (
                        <Typography variant='body2' color='warning.main' sx={{ mt: 2 }}>
                           Limited to {formData.memberLimit} members
                        </Typography>
                     )}

                     {!formData.active && (
                        <Typography variant='body2' color='error' sx={{ mt: 2 }}>
                           This tier is currently inactive and not visible to members
                        </Typography>
                     )}
                  </CardContent>
               </Card>
            </Paper>
         </Grid>
      </Grid>
   );
};

export default SubscriptionTierForm;
